import { useEffect, useState } from 'react';
import { useCompany } from '../context/CompanyContext';
import { INDIA_STATES } from '../constants/indiaStates';
import GstinLookup from './GstinLookup';
import api from '../api';

const CIN_TYPES = ['pvt_ltd', 'llp', 'public_ltd', 'section8'];

const COMPANY_TYPE_LABELS = {
  pvt_ltd: 'Private Limited',
  llp: 'LLP',
  proprietorship: 'Proprietorship',
  partnership: 'Partnership',
  public_ltd: 'Public Limited',
  section8: 'Section 8',
};

const EMPTY_FORM = {
  name: '', company_type: 'pvt_ltd', gstin: '', pan: '', cin: '',
  email: '', mobile: '',
  state: '', fy_end_month: 'March', registration_date: '', gst_filing_frequency: 'Monthly',
  client_mode: 'Professional', business_structure: 'Solo Company',
};

function CompanyModal() {
  const { modalState, closeModal, refreshCompanies, setSelectedCompanyId, setToast } = useCompany();
  const { open, mode, company } = modalState;

  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && company) {
      setForm({
        name: company.name || '',
        // Not defaulted to pvt_ltd: a signup-created company genuinely has no
        // type yet, and showing one it does not have is how it ended up
        // looking hardcoded to Private Limited.
        company_type: company.company_type || '',
        gstin: company.gstin || '',
        pan: company.pan || '',
        cin: company.cin || '',
        email: company.email || '',
        mobile: company.mobile || '',
        state: company.state || '',
        fy_end_month: company.fy_end_month || 'March',
        registration_date: company.registration_date || '',
        gst_filing_frequency: company.gst_filing_frequency || 'Monthly',
        client_mode: company.client_mode || 'Professional',
        business_structure: company.business_structure || 'Solo Company',
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setError(null);
  }, [open, mode, company]);

  if (!open) return null;

  const showCin = CIN_TYPES.includes(form.company_type);
  const typeChanged = mode === 'edit' && company && form.company_type !== (company.company_type || '');
  const modeChanged = mode === 'edit' && company && form.client_mode !== (company.client_mode || 'Professional');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.pan.trim()) {
      setError('Company Name and PAN are required.');
      return;
    }

    setSaving(true);
    setError(null);

    const payload = {
      name: form.name,
      company_type: form.company_type,
      gstin: form.gstin || null,
      pan: form.pan,
      cin: showCin ? form.cin || null : null,
      email: form.email || null,
      mobile: form.mobile || null,
      state: form.state || null,
      fy_end_month: form.fy_end_month,
      registration_date: form.registration_date || null,
      gst_filing_frequency: form.gst_filing_frequency,
      client_mode: form.client_mode,
      business_structure: form.client_mode === 'Business Owner' ? form.business_structure : null,
    };

    const request =
      mode === 'edit' ? api.put(`/companies/${company.id}`, payload) : api.post('/companies', payload);

    request
      .then((res) =>
        refreshCompanies().then(() => {
          if (mode === 'add') {
            setSelectedCompanyId(res.data.id);
            setToast('Company added successfully! Compliance calendar generated.');
          } else {
            setToast('Company details updated ✓');
          }
          closeModal();
        })
      )
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setSaving(false));
  };

  return (
    <div className="ncf-modal-overlay" onClick={closeModal}>
      <div className="ncf-modal company-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="ncf-modal-title">{mode === 'edit' ? 'Edit Company' : 'Add New Company'}</h3>

        {error && <p className="dash-error">{error}</p>}

        <form onSubmit={handleSubmit}>
          <div className="field-group">
            <span className="field-label">Company Name *</span>
            <input
              className="field-input"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>

          <div className="field-row">
            <div className="field-group">
              <span className="field-label">Company Type (Constitution) *</span>
              <select
                className="field-select"
                required
                value={form.company_type}
                onChange={(e) => setForm({ ...form, company_type: e.target.value })}
              >
                {/* A company created through public signup has no type set, so
                    this must not silently read as Private Limited - the value
                    decides which ROC forms get generated. */}
                <option value="">Select company type</option>
                <option value="pvt_ltd">Private Limited</option>
                <option value="llp">LLP</option>
                <option value="proprietorship">Proprietorship</option>
                <option value="partnership">Partnership</option>
                <option value="public_ltd">Public Limited</option>
                <option value="section8">Section 8</option>
              </select>
            </div>
            <div className="field-group">
              <span className="field-label">Financial Year End</span>
              <select
                className="field-select"
                value={form.fy_end_month}
                onChange={(e) => setForm({ ...form, fy_end_month: e.target.value })}
              >
                <option value="March">March</option>
                <option value="December">December</option>
              </select>
            </div>
          </div>

          {mode === 'edit' && typeChanged && (
            <p className="dash-error" style={{ marginTop: -8, marginBottom: 14 }}>
              Company type drives which ROC forms apply, and the existing calendar was built under the
              old type. After saving, use <strong>Rebuild compliance calendar</strong> on the Manage
              Companies list to apply it — already-filed items are never touched.
            </p>
          )}

          <div className="field-row">
            <div className="field-group">
              <span className="field-label">Usage Type</span>
              <select
                className="field-select"
                value={form.client_mode}
                onChange={(e) => setForm({ ...form, client_mode: e.target.value })}
              >
                <option value="Professional">Professional (CA/consultant managing multiple clients)</option>
                <option value="Business Owner">Business Owner (tracking own company directly)</option>
              </select>
            </div>
            {form.client_mode === 'Business Owner' && (
              <div className="field-group">
                <span className="field-label">Business Structure</span>
                <select
                  className="field-select"
                  value={form.business_structure || 'Solo Company'}
                  onChange={(e) => setForm({ ...form, business_structure: e.target.value })}
                >
                  <option value="Solo Company">Solo Company</option>
                  <option value="Group Companies">Group Companies</option>
                </select>
              </div>
            )}
          </div>

          {mode === 'edit' && modeChanged && (
            <p className="dash-error" style={{ marginTop: -8, marginBottom: 14 }}>
              {form.client_mode === 'Professional'
                ? 'Switching to Professional shows Client Master, and ITR / Audit / DSC / Documents gain a client field. Existing records stay exactly as they are — they simply show as not linked to a client until you assign one.'
                : 'Switching to Business Owner hides Client Master. Nothing is deleted, but any record already linked to a client stays linked and that link will no longer be visible on screen.'}
            </p>
          )}
          {mode === 'add' && form.client_mode === 'Business Owner' && (
            <p className="dash-table-sub" style={{ marginTop: -8, marginBottom: 14 }}>
              Client Master will be hidden for this company. DSC Register, ITR Filing Status, Audit
              Status, and Documents attach directly to this company — no client selection step. For
              Group Companies, add each related company separately and switch between them using the
              company selector.
            </p>
          )}

          <div className="field-group">
            <span className="field-label">GST Filing Frequency</span>
            <select
              className="field-select"
              value={form.gst_filing_frequency}
              onChange={(e) => setForm({ ...form, gst_filing_frequency: e.target.value })}
            >
              <option value="Monthly">Monthly</option>
              <option value="Quarterly">Quarterly (QRMP)</option>
            </select>
            {mode === 'edit' && (
              <p className="dash-table-sub" style={{ marginTop: 6, marginBottom: 0 }}>
                Only affects compliance items generated after this change — already-generated
                or already-filed items are not altered.
              </p>
            )}
          </div>

          <div className="field-group">
            <span className="field-label">GSTIN</span>
            <input
              className="field-input"
              value={form.gstin}
              onChange={(e) => setForm({ ...form, gstin: e.target.value })}
            />
            <GstinLookup
              gstin={form.gstin}
              variant="button"
              onFetched={(data) =>
                setForm((f) => ({
                  ...f,
                  name: data.legal_name || data.trade_name || f.name,
                  state: data.state || f.state,
                }))
              }
            />
          </div>

          <div className="field-row">
            <div className="field-group">
              <span className="field-label">PAN *</span>
              <input
                className="field-input"
                required
                value={form.pan}
                onChange={(e) => setForm({ ...form, pan: e.target.value })}
              />
            </div>
            {showCin && (
              <div className="field-group">
                <span className="field-label">CIN</span>
                <input
                  className="field-input"
                  value={form.cin}
                  onChange={(e) => setForm({ ...form, cin: e.target.value })}
                />
              </div>
            )}
          </div>

          <div className="field-row">
            <div className="field-group">
              <span className="field-label">Email</span>
              <input
                type="email"
                className="field-input"
                placeholder="Used as the default Alert Preferences recipient"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="field-group">
              <span className="field-label">Mobile</span>
              <input
                type="tel"
                className="field-input"
                value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value })}
              />
            </div>
          </div>

          <div className="field-row">
            <div className="field-group">
              <span className="field-label">State</span>
              <select
                className="field-select"
                value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })}
              >
                <option value="">Select state</option>
                {INDIA_STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="field-group">
              <span className="field-label">Registration Date</span>
              <input
                type="date"
                className="field-input"
                value={form.registration_date || ''}
                onChange={(e) => setForm({ ...form, registration_date: e.target.value })}
              />
            </div>
          </div>

          <div className="ncf-modal-actions">
            <button type="button" className="dash-btn cal-btn-grey" onClick={closeModal}>
              Cancel
            </button>
            <button type="submit" className="dash-btn dash-btn-orange" disabled={saving}>
              {saving ? 'Saving...' : mode === 'edit' ? 'Save Changes' : 'Save & Generate Calendar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CompanyModal;
